import { useState, useEffect, useRef } from 'react';
import debug from '@commons-shared/debug';
import { Button } from './';
import { config } from '../config/environment';
import '../styles/message-colors.css';

// Historique global pour le comptage des messages successifs identiques
// Utilisation d'un objet global pour persister entre les instances du composant
const globalMessageHistory = { type: '', text: '', count: 0 };

/**
 * Composant MessageLog - Affiche des messages à l'utilisateur et les log via debug
 * Se réaffiche systématiquement même si le message est identique au précédent
 * Affiche un compteur pour les messages successifs identiques
 * @param {Object} props
 * @param {string} props.type - Type de message ('error', 'success', 'info', 'warning')
 * @param {string} props.text - Texte du message
 * @param {boolean} props.autoHide - Si true, le message se masque automatiquement après un délai
 * @param {number} props.duration - Durée en ms avant que le message ne se masque (si autoHide est true)
 */
export default function MessageLog({ type = '', text = '', autoHide = false, duration = 5000 }) {
  const [visible, setVisible] = useState(false);
  const [displayCount, setDisplayCount] = useState(0);
  const lastUpdateRef = useRef(0);
  const processedRef = useRef({ type: '', text: '', timestamp: 0 });

  useEffect(() => {
    if (text) {
      // Marquer le timestamp actuel pour forcer une nouvelle animation
      const currentTime = Date.now();
      
      // Éviter la double exécution en mode strict de React
      // Vérifier si on a déjà traité ce message exact récemment (dans les 50ms)
      const lastProcessed = processedRef.current;
      if (lastProcessed.type === type && 
          lastProcessed.text === text && 
          (currentTime - lastProcessed.timestamp) < 50) {
        return; // Ne pas traiter le même message deux fois rapidement
      }
      
      // Marquer ce message comme traité
      processedRef.current = { type, text, timestamp: currentTime };
      lastUpdateRef.current = currentTime;
      
      // Vérifier si c'est le même message que le précédent (et non vide)
      const isSameMessage = globalMessageHistory.type === type && 
                           globalMessageHistory.text === text && 
                           globalMessageHistory.text !== '';
      
      if (isSameMessage) {
        // Incrémenter le compteur pour le même message
        globalMessageHistory.count += 1;
      } else {
        // Nouveau message différent, réinitialiser l'historique
        globalMessageHistory.type = type;
        globalMessageHistory.text = text;
        globalMessageHistory.count = 1;
      }
      
      // Mettre à jour le compteur d'affichage
      setDisplayCount(globalMessageHistory.count);
      
      // Toujours rendre visible quand un nouveau message arrive
      setVisible(true);
      
      // Logger le message en fonction du type
      const logMessage = globalMessageHistory.count > 1 ? `(${globalMessageHistory.count}x) ${text}` : text;
      switch (type) {
        case 'error':
          debug.error(config, logMessage);
          break;
        case 'warning':
          debug.warn(config, logMessage);
          break;
        case 'success':
          debug.success(config, logMessage);
          break;
        case 'info':
        default:
          debug.log(config, logMessage);
          break;
      }
      
      // Masquer automatiquement après la durée spécifiée si autoHide est true
      if (autoHide && duration > 0) {
        const timer = setTimeout(() => {
          // Vérifier que ce timer correspond au dernier message
          if (lastUpdateRef.current === currentTime) {
            setVisible(false);
          }
        }, duration);
        
        return () => clearTimeout(timer);
      }
    } else {
      setVisible(false);
      // Réinitialiser l'historique quand il n'y a pas de message
      globalMessageHistory.type = '';
      globalMessageHistory.text = '';
      globalMessageHistory.count = 0;
      setDisplayCount(0);
    }
  }, [text, type, autoHide, duration]); // Ne pas inclure internalKey car cela créerait une boucle

  // Si pas de texte ou message masqué, ne rien afficher
  if (!visible || !text) {
    return null;
  }

  // Définir les classes en fonction du type de message
  const getTypeClasses = () => {
    switch (type) {
      case 'error':
        return 'message-log-error';
      case 'warning':
        return 'message-log-warning';
      case 'success':
        return 'message-log-success';
      case 'info':
      default:
        return 'message-log-info';
    }
  };

  // Définir l'icône en fonction du type de message
  const getTypeIcon = () => {
    switch (type) {
      case 'error':
        return (
          <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      case 'success':
        return (
          <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        );
      case 'info':
      default:
        return (
          <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  // Rendu du composant
  return (
    <div className={`fixed bottom-4 left-1/2 transform -translate-x-1/2 w-auto max-w-md p-4 rounded-md shadow-lg ${getTypeClasses()}`}>
      <div className="flex">
        <div className="flex-shrink-0">
          {getTypeIcon()}
        </div>
        <div className="ml-3">
          <p className="text-sm font-medium">
            {displayCount > 1 && (
              <span className="inline-block bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded-full mr-2 font-bold">
                ({displayCount}x)
              </span>
            )}
            {text}
          </p>
        </div>
        <Button 
          variant="ghost"
          icon
          className="ml-auto -mx-1.5 -my-1.5"
          onClick={() => setVisible(false)}
          aria-label="Close"
        >
          <span className="sr-only">Close</span>
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
          </svg>
        </Button>
      </div>
    </div>
  );
}
